import { Button, Grid, Typography, Link, Tooltip, IconButton } from "@mui/material";
import BaseAutocomplete from "components/Shared/Base/BaseAutocomplete";
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import templateFile from "assets/SAMPLE_CAN_TMS_SHIPMENT_IMPORT.xlsx";
import { useLazyGetDdlVendorProfileQuery } from "store/services/vendorProfileApi";
import * as XLSX from "xlsx";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { FileUploadOutlined, } from "@mui/icons-material";
import { getRandomString } from "../../../../../utils/getRandomString";
import { useAuth } from "hooks";
import { useLazyGetBranchListQuery } from "store/services/branchApi";

const UploadExcelShipment = () => {
  const [showDialog, setShowDialog] = useState(false);
  const [fetchBranches] = useLazyGetBranchListQuery();
  const [fileData, setFileData] = useState(null);
  const navigate = useNavigate();
  const { isVendor, isVendorStaff } = useAuth();

  const { control, setValue } = useForm();

  const [fetchVendors, { data: vendors, isLoading: loadingVendors }] =
    useLazyGetDdlVendorProfileQuery();
  useEffect(() => {
    if (!showDialog) return
    fetchVendors();
  }, [fetchVendors, showDialog]);

  useEffect(() => {
    if (isVendor && vendors && vendors.length > 0) {
      setValue("vendor", vendors[0]);
    }
  }, [isVendor, vendors, setValue]);

  const downloadTemplate = async () => {
    try {
      // Step 1: Fetch the original Excel template as a blob
      const response = await fetch(templateFile);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: "array" });

      // Step 2: Fetch branch list from API
      const branchResponse = await fetchBranches({ page: 1, limit: 1000 }).unwrap();
      const branchList = branchResponse?.data ?? [];

      // Step 3: Convert branch data to JSON format for Excel
      const branchDataForSheet = branchList.map((branch) => ({
        Name: branch?.name || "",
        Code: branch?.code || "",
      }));

      // Step 4: Convert JSON to worksheet and append it to workbook
      const branchSheet = XLSX.utils.json_to_sheet(branchDataForSheet);
      if (workbook.SheetNames.includes("BranchList")) {
        delete workbook.Sheets["BranchList"];
        workbook.SheetNames = workbook.SheetNames.filter((name) => name !== "BranchList");
      }
      XLSX.utils.book_append_sheet(workbook, branchSheet, "BranchList");

      // Step 5: Write the updated workbook to blob and trigger download
      const updatedWorkbookArray = XLSX.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });

      const updatedBlob = new Blob([updatedWorkbookArray], {
        type: "application/octet-stream",
      });

      const url = window.URL.createObjectURL(updatedBlob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "BulkImportShipmentsTemplate_With_BranchList.xlsx");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating updated Excel template:", error);
      alert("Failed to download the Excel file with branch list.");
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileExtension = file.name.split(".").pop().toLowerCase();

      const reader = new FileReader();
      reader.onload = (e) => {
        let workbook;
        try {
          if (fileExtension === "csv") {
            const csvData = e.target.result;
            workbook = XLSX.read(csvData, { type: "string" });
          } else {
            const binaryData = new Uint8Array(e.target.result);
            workbook = XLSX.read(binaryData, { type: "array" });
          }

          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: "" });

          const updatedJsonData = jsonData.map((row) => {
            let shipmentDetails = [];
            try {
              if (row["Shipment Details"]) {
                shipmentDetails = JSON.parse(row["Shipment Details"]);
                shipmentDetails = Array.isArray(shipmentDetails)
                  ? shipmentDetails.map((item) => ({
                    itemReference: item.itemReference || "",
                    name: item.name || "",
                    dimensions: {
                      length: Number(item.dimensions?.length) || null,
                      width: Number(item.dimensions?.width) || null,
                      height: Number(item.dimensions?.height) || null,
                    },
                    weight: Number(item.weight) || null,
                  }))
                  : [];
              }
            } catch (error) {
              console.error(`Error parsing Shipment Details for Shipment #${row["Shipment #"]}:`, error);
              shipmentDetails = [
                {
                  itemReference: "",
                  name: "",
                  dimensions: { length: null, width: null, height: null },
                  weight: null,
                },
              ];
            }

            return {
              ...row,
              "Shipment #": row["Shipment #"] || `SH-${getRandomString(10)}`,
              "Shipment Details": shipmentDetails,
            };
          });

          setFileData(updatedJsonData);
        } catch (err) {
          console.error("Error parsing uploaded file:", err);
          alert("Invalid file format. Please upload a valid Excel or CSV file.");
        }
      };

      reader.onerror = () => {
        console.error("Error reading file");
        alert("Error reading the file. Please try again.");
      };

      // Read as text for CSV; arrayBuffer for Excel
      if (fileExtension === "csv") {
        reader.readAsText(file); // ✅ CSV
      } else {
        reader.readAsArrayBuffer(file); // ✅ Excel
      }
    } else {
      alert("Please upload a file.");
    }
  };


  const handleReview = () => {
    const selectedVendor = control._formValues.vendor;
    if (!selectedVendor?._id) {
      alert("Please select a Vendor before proceeding.");
      return;
    }

    if (fileData && fileData.length > 0) {
      let path = "/app/crm/shipment/importShipmentExcel/review";

      if (isVendor) {
        path = "/app/vendor/vendorBulkImport";
      } else if (isVendorStaff) {
        path = "/app/staff/staffBulkImport";
      }

      navigate(path, {
        state: { shipments: fileData, vendor: selectedVendor },
      });
    } else {
      alert("Please upload a valid Excel file first.");
    }
  }

  const handleClose = () => {
    setShowDialog(false);
  };

  return (
    <>
      <Tooltip title="Import Bulk Shipment" arrow>
        <IconButton color="primary" onClick={() => setShowDialog(true)}>
          <FileUploadOutlined />
        </IconButton>
      </Tooltip>

      <Dialog open={showDialog} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Import Excel Shipments</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <Controller
                name="vendor"
                control={control}
                rules={{ required: "Please select a Vendor" }}
                render={({ field }) => (
                  <BaseAutocomplete
                    fullWidth
                    required
                    label="Vendor"
                    loading={loadingVendors}
                    loadingText="Loading Vendor..."
                    options={vendors || []}
                    getOptionLabel={(option) =>
                      option ? `${option?.shopName} - ${option?.ownerName}` : ""
                    }
                    isOptionEqualToValue={(option, value) =>
                      option?._id === value?._id
                    }
                    value={field?.value ?? ""}
                    onChange={(_, data) => {
                      setValue("vendor", data);
                    }}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body1">Upload File *</Typography>
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileUpload}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography>
                Click here to download the Template for Bulk Import Orders
              </Typography>
              <Link
                component="button"
                onClick={downloadTemplate}
                style={{ cursor: "pointer", textDecoration: "none" }}
              >
                Download
              </Link>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReview} variant="contained">
            Review
          </Button>
          <Button onClick={handleClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UploadExcelShipment;
